// The messaging engine: the only place that combines keys, the local store, and the
// network. It bootstraps X3DH sessions, drives the Double Ratchet for 1:1, manages
// group sender keys, and applies deletes / purges / disappearing messages. All
// plaintext exists only transiently here and in the encrypted local store.
import {
  genX25519,
  x3dhInitiate,
  x3dhRespond,
  initInitiator,
  initResponder,
  ratchetEncrypt,
  ratchetDecrypt,
  serializeRatchet,
  deserializeRatchet,
  createSenderKey,
  groupEncrypt,
  groupDecrypt,
  exportSenderKey,
  importSenderKey,
  serializeSender,
  deserializeSender,
  serializeReceiver,
  deserializeReceiver,
  signTombstone,
  signPurge,
  safetyNumber,
  aeadSeal,
  aeadOpen,
  randomBytes,
  type RatchetState,
  type UnwrappedBundle,
  type PeerBundle,
  type X3dhInitBytes,
} from "../crypto/index.js";
import { toB64, fromB64, utf8, fromUtf8 } from "@shared/encoding.js";
import type { DmHeader, GroupHeader, MessageEnvelope, ServerEvent, PrekeyBundle } from "@shared/wire.js";
import { Api } from "../net/api.js";
import { ConvSocket } from "../net/socket.js";
import {
  LocalStore,
  type StoredConversation,
  type StoredMessage,
  type StoredSession,
  type StoredGroup,
  type StoredFile,
  type Settings,
} from "../store/idb.js";

export interface CurrentUser {
  user_id: string;
  handle: string;
}

type Content = { kind: "text"; text: string } | { kind: "file"; file: StoredFile };

export type PurgeWindow = "hour" | "day" | "all";

const peerOf = (conv: StoredConversation, me: string) => conv.members.find((m) => m.user_id !== me);

function bundleToPeer(b: PrekeyBundle): PeerBundle {
  return {
    ik_dh_pub: fromB64(b.ik_dh_pub),
    ik_sig_pub: fromB64(b.ik_sig_pub),
    spk_id: b.spk_id,
    spk_pub: fromB64(b.spk_pub),
    spk_sig: fromB64(b.spk_sig),
    opk_id: b.opk_id,
    opk_pub: b.opk_pub ? fromB64(b.opk_pub) : null,
  };
}

const initToBytes = (i: NonNullable<DmHeader["x3dh"]>): X3dhInitBytes => ({
  ik_dh: fromB64(i.ik_dh),
  ik_sig: fromB64(i.ik_sig),
  ek: fromB64(i.ek),
  spk_id: i.spk_id,
  opk_id: i.opk_id,
});

export class Engine {
  constructor(
    public me: CurrentUser,
    private bundle: UnwrappedBundle,
    private api: Api,
    private store: LocalStore,
    private onChange: (convId: string) => void,
  ) {}

  // ---- Conversation bookkeeping ----

  async syncConversations(): Promise<StoredConversation[]> {
    const { conversations } = await this.api.listConv();
    for (const c of conversations) {
      const prev = await this.store.getConversation(c.conv_id);
      await this.store.putConversation({
        conv_id: c.conv_id,
        type: c.type,
        title: prev?.title ?? null,
        members: c.members,
        disappearing_ttl: c.disappearing_ttl,
        last_seq: prev?.last_seq ?? 0,
        last_keydist_id: prev?.last_keydist_id ?? 0,
        last_tomb: prev?.last_tomb ?? 0,
      });
    }
    return this.store.allConversations();
  }

  async startDm(handle: string): Promise<string> {
    const peer = await this.api.lookup(handle);
    if (peer.user_id === this.me.user_id) throw new Error("That's you - search for someone else.");
    // Reuse an existing DM with this peer if we have one.
    for (const c of await this.store.allConversations()) {
      if (c.type === "dm" && c.members.some((m) => m.user_id === peer.user_id)) return c.conv_id;
    }
    const { conv_id } = await this.api.createConv("dm", [peer.user_id]);
    await this.syncConversations();
    return conv_id;
  }

  async createGroup(title: string, handles: string[]): Promise<string> {
    const ids: string[] = [];
    for (const h of handles) ids.push((await this.api.lookup(h.trim())).user_id);
    const { conv_id } = await this.api.createConv("group", ids);
    await this.syncConversations();
    const conv = await this.store.getConversation(conv_id);
    if (conv) {
      conv.title = title;
      await this.store.putConversation(conv);
    }
    return conv_id;
  }

  // ---- Sending ----

  async sendText(convId: string, text: string): Promise<void> {
    const conv = await this.store.getConversation(convId);
    if (!conv) throw new Error("conversation not found");
    await (conv.type === "dm" ? this.sendDm(conv, { kind: "text", text }) : this.sendGroup(conv, { kind: "text", text }));
  }

  async sendFile(convId: string, file: { name: string; mime: string; bytes: Uint8Array }): Promise<void> {
    const conv = await this.store.getConversation(convId);
    if (!conv) throw new Error("conversation not found");
    // Encrypt the attachment under a fresh key; only the (encrypted-in-message) key opens it.
    const key = randomBytes(32);
    const { nonce, ct } = aeadSeal(key, file.bytes);
    const packed = new Uint8Array(nonce.length + ct.length);
    packed.set(nonce, 0);
    packed.set(ct, nonce.length);
    const { blob_key } = await this.api.uploadAttachment(convId, packed);
    const stored: StoredFile = { name: file.name, mime: file.mime, size: file.bytes.length, blob_key, key: toB64(key) };
    await (conv.type === "dm" ? this.sendDm(conv, { kind: "file", file: stored }) : this.sendGroup(conv, { kind: "file", file: stored }));
  }

  private async sendDm(conv: StoredConversation, content: Content): Promise<void> {
    const peer = peerOf(conv, this.me.user_id);
    if (!peer) throw new Error("no peer in this chat");
    const { session, stored } = await this.initiatorSession(peer.user_id);
    const enc = ratchetEncrypt(session, utf8(JSON.stringify(content)));
    const header: DmHeader = { ...enc.header, t: "dm" };
    if (!stored.established && stored.pendingInit) header.x3dh = stored.pendingInit;

    const res = await this.api.send(conv.conv_id, { header, ciphertext: enc.ct, ttl_sec: conv.disappearing_ttl });
    stored.state = serializeRatchet(session);
    await this.store.putSession(stored);
    await this.recordOutgoing(conv, res.msg_id, res.seq, res.created_at, content);
  }

  private async sendGroup(conv: StoredConversation, content: Content): Promise<void> {
    const g = await this.ensureGroupSelf(conv);
    const sender = deserializeSender(g.mySender!);
    const enc = groupEncrypt(sender, utf8(JSON.stringify(content)));
    g.mySender = serializeSender(sender);
    await this.store.putGroup(g);
    const { ct, ...header } = enc;
    const res = await this.api.send(conv.conv_id, { header: header as GroupHeader, ciphertext: ct, ttl_sec: conv.disappearing_ttl });
    await this.recordOutgoing(conv, res.msg_id, res.seq, res.created_at, content);
  }

  private async recordOutgoing(conv: StoredConversation, msgId: string, seq: number, ts: number, content: Content): Promise<void> {
    const expires_at = conv.disappearing_ttl > 0 ? ts + conv.disappearing_ttl * 1000 : null;
    await this.store.putMessage({
      msg_id: msgId,
      conv_id: conv.conv_id,
      seq,
      sender_id: this.me.user_id,
      outgoing: true,
      kind: content.kind,
      text: content.kind === "text" ? content.text : undefined,
      file: content.kind === "file" ? content.file : undefined,
      ts,
      expires_at,
    });
    conv.last_seq = Math.max(conv.last_seq, seq);
    await this.store.putConversation(conv);
    this.onChange(conv.conv_id);
  }

  // ---- Pairwise session helpers ----

  private async initiatorSession(peerId: string): Promise<{ session: RatchetState; stored: StoredSession }> {
    const existing = await this.store.getSession(peerId);
    if (existing) return { session: deserializeRatchet(existing.state), stored: existing };
    const bundle = await this.api.prekeys(peerId);
    const init = x3dhInitiate(this.bundle.identity, bundleToPeer(bundle), genX25519());
    const session = initInitiator(init.sk, init.remoteRatchetPub, init.ad);
    const stored: StoredSession = {
      peer_id: peerId,
      state: serializeRatchet(session),
      established: false,
      pendingInit: {
        ik_dh: toB64(init.init.ik_dh),
        ik_sig: toB64(init.init.ik_sig),
        ek: toB64(init.init.ek),
        spk_id: init.init.spk_id,
        opk_id: init.init.opk_id,
      },
    };
    return { session, stored };
  }

  private async decryptFromPeer(peerId: string, header: DmHeader, ct: string): Promise<Uint8Array> {
    let stored = await this.store.getSession(peerId);
    let session: RatchetState;
    if (!stored) {
      if (!header.x3dh) throw new Error("no session and no handshake header");
      const init = initToBytes(header.x3dh);
      const opk = init.opk_id != null ? (this.bundle.opks.find((o) => o.id === init.opk_id)?.keypair ?? null) : null;
      const resp = x3dhRespond(this.bundle.identity, this.bundle.spk.keypair, opk, init);
      session = initResponder(resp.sk, this.bundle.spk.keypair, resp.ad);
      stored = { peer_id: peerId, state: serializeRatchet(session), established: true };
    } else {
      session = deserializeRatchet(stored.state);
      stored.established = true;
      stored.pendingInit = undefined;
    }
    const pt = ratchetDecrypt(session, header, ct);
    stored.state = serializeRatchet(session);
    await this.store.putSession(stored);
    return pt;
  }

  // ---- Incoming messages ----

  private async handleEnvelope(conv: StoredConversation, env: MessageEnvelope): Promise<void> {
    if (env.sender_id === this.me.user_id) {
      conv.last_seq = Math.max(conv.last_seq, env.seq);
      await this.store.putConversation(conv);
      return; // our own echo; already stored on send
    }
    if (await this.store.getMessage(env.msg_id)) return;

    let pt: Uint8Array;
    try {
      pt = conv.type === "dm" ? await this.decryptFromPeer(env.sender_id, env.header as DmHeader, env.ciphertext) : await this.groupDecrypt(conv, env);
    } catch (err) {
      console.warn("could not decrypt message", env.msg_id, err);
      return;
    }
    const content = JSON.parse(fromUtf8(pt)) as Content;
    await this.store.putMessage({
      msg_id: env.msg_id,
      conv_id: conv.conv_id,
      seq: env.seq,
      sender_id: env.sender_id,
      outgoing: false,
      kind: content.kind,
      text: content.kind === "text" ? content.text : undefined,
      file: content.kind === "file" ? content.file : undefined,
      ts: env.created_at,
      expires_at: env.expires_at,
    });
    conv.last_seq = Math.max(conv.last_seq, env.seq);
    await this.store.putConversation(conv);
    this.onChange(conv.conv_id);
  }

  // ---- Groups ----

  private async ensureGroupSelf(conv: StoredConversation): Promise<StoredGroup> {
    let g = (await this.store.getGroup(conv.conv_id)) ?? { conv_id: conv.conv_id, epoch: 1, mySender: null, receivers: {} };
    if (!g.mySender) {
      const sk = createSenderKey(g.epoch);
      g.mySender = serializeSender(sk);
      await this.store.putGroup(g);
      await this.distribute(conv, sk.epoch);
    }
    return g;
  }

  private async distribute(conv: StoredConversation, epoch: number): Promise<void> {
    const g = await this.store.getGroup(conv.conv_id);
    if (!g?.mySender) return;
    const dist = exportSenderKey(deserializeSender(g.mySender));
    for (const m of conv.members) {
      if (m.user_id === this.me.user_id) continue;
      const { session, stored } = await this.initiatorSession(m.user_id);
      const enc = ratchetEncrypt(session, utf8(JSON.stringify(dist)));
      const header: DmHeader = { ...enc.header, t: "dm" };
      if (!stored.established && stored.pendingInit) header.x3dh = stored.pendingInit;
      stored.state = serializeRatchet(session);
      await this.store.putSession(stored);
      await this.api.postKeydist(conv.conv_id, m.user_id, epoch, toB64(utf8(JSON.stringify({ header, ct: enc.ct }))));
    }
  }

  private async applyKeydist(conv: StoredConversation): Promise<void> {
    const { distributions } = await this.api.getKeydist(conv.conv_id, conv.last_keydist_id);
    if (distributions.length === 0) return;
    const g = (await this.store.getGroup(conv.conv_id)) ?? { conv_id: conv.conv_id, epoch: 1, mySender: null, receivers: {} };
    let maxId = conv.last_keydist_id;
    for (const d of distributions) {
      maxId = Math.max(maxId, d.id);
      try {
        const { header, ct } = JSON.parse(fromUtf8(fromB64(d.ciphertext))) as { header: DmHeader; ct: string };
        const dist = JSON.parse(fromUtf8(await this.decryptFromPeer(d.from_user, header, ct)));
        const existing = g.receivers[d.from_user];
        if (!existing || dist.epoch >= existing.epoch) g.receivers[d.from_user] = serializeReceiver(importSenderKey(dist));
      } catch (err) {
        console.warn("bad key distribution from", d.from_user, err);
      }
    }
    await this.store.putGroup(g);
    conv.last_keydist_id = maxId;
    await this.store.putConversation(conv);
  }

  private async groupDecrypt(conv: StoredConversation, env: MessageEnvelope): Promise<Uint8Array> {
    const header = env.header as GroupHeader;
    let g = await this.store.getGroup(conv.conv_id);
    if (!g?.receivers[env.sender_id] || g.receivers[env.sender_id].epoch !== header.epoch) {
      await this.applyKeydist(conv);
      g = await this.store.getGroup(conv.conv_id);
    }
    const recvSer = g?.receivers[env.sender_id];
    if (!recvSer || recvSer.epoch !== header.epoch) throw new Error("no sender key for this epoch yet");
    const recv = deserializeReceiver(recvSer);
    const pt = groupDecrypt(recv, header, env.ciphertext);
    g!.receivers[env.sender_id] = serializeReceiver(recv);
    await this.store.putGroup(g!);
    return pt;
  }

  private async rotateGroup(conv: StoredConversation): Promise<void> {
    const g = (await this.store.getGroup(conv.conv_id)) ?? { conv_id: conv.conv_id, epoch: 1, mySender: null, receivers: {} };
    const epoch = g.epoch + 1;
    g.epoch = epoch;
    g.mySender = serializeSender(createSenderKey(epoch));
    await this.store.putGroup(g);
    await this.distribute(conv, epoch);
  }

  // ---- Membership (admin) ----

  async addMember(convId: string, handle: string): Promise<void> {
    const peer = await this.api.lookup(handle);
    await this.api.addMember(convId, peer.user_id);
    await this.refreshAndRotate(convId);
  }
  async removeMember(convId: string, userId: string): Promise<void> {
    await this.api.removeMember(convId, userId);
    await this.refreshAndRotate(convId);
  }
  private async refreshAndRotate(convId: string): Promise<void> {
    const fresh = await this.api.getConv(convId);
    const conv = await this.store.getConversation(convId);
    if (conv) {
      conv.members = fresh.members;
      await this.store.putConversation(conv);
      await this.rotateGroup(conv);
      this.onChange(convId);
    }
  }

  // ---- Deletion, purge, disappearing ----

  async deleteForEveryone(convId: string, msgId: string): Promise<void> {
    const m = await this.store.getMessage(msgId);
    if (!m || !m.outgoing) throw new Error("You can only delete your own messages for everyone.");
    const tomb = signTombstone(this.bundle.identity.ik_sig.priv, { conv_id: convId, msg_id: msgId, sender_id: this.me.user_id, ts: Date.now() });
    await this.api.tombstone(convId, tomb);
    await this.store.deleteMessage(msgId); // gone, not a placeholder
    this.onChange(convId);
  }

  /** Insert a local-only system line (e.g. a screenshot notice). Never sent to the server. */
  async addSystemMessage(convId: string, text: string): Promise<void> {
    const conv = await this.store.getConversation(convId);
    await this.store.putMessage({
      msg_id: `sys-${crypto.randomUUID()}`,
      conv_id: convId,
      seq: conv?.last_seq ?? 0,
      sender_id: "system",
      outgoing: false,
      kind: "system",
      text,
      ts: Date.now(),
      expires_at: null,
    });
    this.onChange(convId);
  }

  async deleteLocal(convId: string, msgId: string): Promise<void> {
    await this.store.deleteMessage(msgId);
    this.onChange(convId);
  }

  async purge(convId: string, window: PurgeWindow, alsoEveryone: boolean): Promise<void> {
    const now = Date.now();
    const after = window === "hour" ? now - 3_600_000 : window === "day" ? now - 86_400_000 : 0;
    await this.store.purgeMessages(convId, (m) => m.ts < after); // keep older than the window
    if (alsoEveryone) {
      const dir = signPurge(this.bundle.identity.ik_sig.priv, { conv_id: convId, sender_id: this.me.user_id, after_ts: after, ts: now });
      await this.api.purge(convId, dir);
    }
    this.onChange(convId);
  }

  async setDisappearing(convId: string, ttlSec: number): Promise<void> {
    await this.api.setTtl(convId, ttlSec);
    const conv = await this.store.getConversation(convId);
    if (conv) {
      conv.disappearing_ttl = ttlSec;
      await this.store.putConversation(conv);
      this.onChange(convId);
    }
  }

  /** Remove messages whose disappearing TTL has elapsed on this device. */
  async sweepExpired(convId: string): Promise<void> {
    const now = Date.now();
    const removed = await this.store.purgeMessages(convId, (m) => !(m.expires_at != null && m.expires_at <= now));
    if (removed.length) this.onChange(convId);
  }

  // ---- WebSocket events ----

  async handleEvent(convId: string, ev: ServerEvent): Promise<void> {
    const conv = await this.store.getConversation(convId);
    if (!conv) return;
    switch (ev.type) {
      case "backlog":
        for (const m of ev.messages) await this.handleEnvelope(conv, m);
        for (const t of ev.tombstones) await this.removeMessage(t.msg_id, convId);
        break;
      case "message":
        await this.handleEnvelope(conv, ev.message);
        break;
      case "tombstone":
        await this.removeMessage(ev.tombstone.msg_id, convId);
        break;
      case "purge":
        await this.store.purgeMessages(convId, (m) => !(m.sender_id === ev.sender_id && m.ts >= ev.after_ts));
        this.onChange(convId);
        break;
      case "gc": {
        const ids = new Set(ev.msg_ids);
        await this.store.purgeMessages(convId, (m) => !ids.has(m.msg_id));
        this.onChange(convId);
        break;
      }
      case "members":
        await this.refreshAndRotate(convId);
        break;
      case "keydist":
        await this.applyKeydist(conv);
        break;
      case "ready":
        break;
    }
  }

  private async removeMessage(msgId: string, convId: string): Promise<void> {
    await this.store.deleteMessage(msgId);
    this.onChange(convId);
  }

  // ---- Reads ----

  messages(convId: string): Promise<StoredMessage[]> {
    return this.store.messagesFor(convId);
  }
  conversations(): Promise<StoredConversation[]> {
    return this.store.allConversations();
  }
  conversation(convId: string): Promise<StoredConversation | null> {
    return this.store.getConversation(convId);
  }

  async attachmentBytes(convId: string, file: StoredFile): Promise<Uint8Array> {
    const packed = await this.api.downloadAttachment(convId, file.blob_key);
    const nonce = packed.slice(0, 24);
    const ct = packed.slice(24);
    return aeadOpen(fromB64(file.key), nonce, ct);
  }

  /** Safety number for verifying a DM peer (or any member) by their identity key. */
  safetyNumberWith(ikSigPubB64: string): string {
    return safetyNumber(this.bundle.identity.ik_sig.pub, fromB64(ikSigPubB64));
  }

  getSettings(): Promise<Settings> {
    return this.store.getSettings();
  }
  saveSettings(s: Settings): Promise<void> {
    return this.store.putSettings(s);
  }

  /**
   * Live connection for a conversation. We always replay from 0 - handleEnvelope
   * short-circuits on messages already stored, so a full backlog on (re)connect is
   * cheap and guarantees nothing is missed.
   */
  connectSocket(convId: string, onEvent: (ev: ServerEvent) => void, sharePresence: boolean): ConvSocket {
    const sock = new ConvSocket(convId, this.api.getToken() ?? "", onEvent, () => ({ since_seq: 0, since_tomb: 0 }), sharePresence);
    sock.connect();
    return sock;
  }
}
